import { type IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { IMod, IUseMods } from "../components/views/mods";
import type { RootNamespace } from "../graphql";

export type ProfileCtx = RootNamespace;

export class MenuItem {
  constructor(
    public route: string | null,
    public icon: IconDefinition,
    public component: unknown,
    public subRoutes?: Array<{
      route: string;
      component: unknown;
    }>,
    /** Optional i18n key overriding the default `ngp/route/{route}` sidebar label. */
    public labelKey?: string,
  ) {}
}

export type MenuSection = {
  name: string;
  items: MenuItem[];
};

export type Menu = MenuSection[];

/** A warning shown below a config field: a translation key and its parameters. */
export interface FieldWarning {
  key: string;
  params?: Record<string, unknown>;
}

/** Returns the warning for a config field's current value, or `null` when there is none. */
export type FieldWarningCheck = (value: unknown) => FieldWarning | null;

export interface ConfigSectionProps {
  name: string;
  important?: boolean;
  /** Checks keyed by field name. `ConfSection` shows their warnings below the control while the customer
   *  edits. A warning is not a commit gate: saving stays possible. */
  fieldWarnings?: Record<string, FieldWarningCheck>;
  visibleIf?: { config: string; field: string; value: string };
}

export interface ConfigSection {
  component: string;
  props?: ConfigSectionProps | Record<string, unknown>;
}

export type ConfigSections = ConfigSection[];

export type StatusPageConfig = {
  showRconPort: boolean;
  showQueryPort: boolean;
  showFtp: boolean;
  // Show the password players join with, read from the user groups in `virtual.userGroups` (Enshrouded).
  showJoinPassword?: boolean;
};

export interface Option {
  label: string;
  value: string;
}

export interface JoinFeature {
  buttonTextKey: string;
  joinFn: () => void;
}

export interface DynamicSection {
  /** Config name where the controller field lives, e.g. "virtual" */
  controllerConfig: string;
  /** Field name whose value selects the dependent config, e.g. "active_world" */
  controllerField: string;
  /** Prefix for the per-item configs in publicConfigs, e.g. "world_" */
  targetConfigPrefix: string;
  /** Alias key written into publicConfigs that components read, e.g. "world" */
  targetConfigAlias: string;
}

export interface ExtraHeaderButton {
  buttonTextKey: string;
  btnFn: () => void;
}

export type GameProfile<ModType extends IMod = IMod, UseModsType extends IUseMods<ModType> = IUseMods<ModType>> = {
  menu: Menu;
  configSections: ConfigSections;
  hasExpertMode?: boolean;
  // Whenever or not the profile should be show the number of active players. On
  // some games, this makes no sense (BungeeCord or "side car" servers).
  hasActivePlayers?: boolean;
  hasActiveMap?: boolean;
  consoleCommands?: string[];
  hasDns: boolean;
  isBeta: boolean;
  statusPageConfig: StatusPageConfig;
  // Optionally add a join button with a specific text and function to execute
  join?: JoinFeature;
  extraHeaderButton?: ExtraHeaderButton;
  // Optionally override the access data shown in the header (only applied if no DNS name exists)
  accessDataFn?: () => string | undefined;
  // Optionally override the access data to be copied in the header.
  copyAccessDataFn?: () => string | undefined;
  useMods?: () => UseModsType;
  /**
   * Show the generic "possibly incompatible" hint on mods not updated in the last 60 days.
   *
   * Defaults to on. Turn it off for games that carry a real compatibility signal: for Project
   * Zomboid the mod's own `versionMin`/`versionMax` and build folders give an exact per-mod verdict,
   * so the date heuristic only adds false alarms next to an authoritative answer.
   */
  staleModWarning?: boolean;
  readOnlyMode?: boolean;
  usesRcon?: boolean;
  hideHeaderIpPort?: boolean;
  /** Hosted in a Dune battlegroup: shows its daily restart time and needs a battlegroup before starting. */
  hasBattlegroups?: boolean;
  dynamicSection?: DynamicSection;
  /** If set, returns an i18n key while commit must be blocked (e.g. invalid cross-field state). */
  commitBlockedMessageKey?: () => string | null;
  /** If set, filters each console message line. Return null to drop the line. */
  consoleFilter?: ((message: string) => string | null) & {
    /** Called when the console history is reset. Use to clear stateful filter state. */
    reset?: () => void;
    /** Called after a quiet period to flush any buffered pending content. */
    flush?: () => string | null;
  };
  /** @deprecated Use consoleFilter.reset instead */
  consoleFilterReset?: () => void;
  /** If set, transforms each raw gRPC console message before it is split on newlines and passed to consoleFilter. */
  consoleMessagePreprocessor?: (message: string) => string;
};
